import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { addMinutes } from 'date-fns';
import {
  ActivityTypeEnum,
  BookingTaxEstimateInterface,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';

import { BookingService } from '~/checkout/services/booking/booking.service';
import { CheckboxAlignEnum } from '~/component/form/checkbox/enums/checkbox-align.enum';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { legalTermsUsageServiceUrlToggle } from '~/toggles/default-values/legal/terms-usage-service-url';
import { LoaderService } from '~/core/services/loader/loader.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ToggleService } from '~/toggles/services/toggle.service';
import { UserService } from '~/auth/services/user/user.service';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { map, mergeMap } from 'rxjs/operators';
import { DisplayPricePipe } from '~/core/pipes/display-price.pipe';
import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { toDecimalPrice, toIntegerPrice } from '~/core/helpers/numbers.helper';
import { CartService } from '~/checkout/services/cart/cart.service';
import { TaxEstimateService } from '~/checkout/services/tax-estimate/tax-estimate.service';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconButtonVariantEnum } from '~/component/buttons/vtmn-play-icon-button/enum/vtmn-play-icon-button-variant.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { BmsTermsInterface } from '~/checkout/components/pages/payment/onepay/payment-onepay.component';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent implements OnInit, OnDestroy {
  @Output() public login = new EventEmitter();
  private readonly subscriptions = new Subscription();

  private isLoggedIn: boolean;

  public readonly checkboxAlign = CheckboxAlignEnum.Top;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayIconButtonVariant = VtmnPlayIconButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public isCartEmpty = true;

  public bmsTerms: BmsTermsInterface[];
  public cartItems: CheckoutItem[];
  public cartSubmissionDisabled = false;
  public consent = false;
  public hasClicked = false;
  public isCartOpen: boolean;
  public lowerSize = false;
  public taxes: BookingTaxEstimateInterface;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public isRedirect: boolean;

  constructor(
    private readonly bookingService: BookingService,
    private readonly cartService: CartService,
    private readonly checkoutService: CheckoutService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly loaderService: LoaderService,
    private readonly redirectionService: RedirectionService,
    private readonly toggleService: ToggleService,
    @Inject(TranslateService)
    @Inject(PLATFORM_ID)
    private readonly platformId: string,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly taxEstimateService: TaxEstimateService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit() {
    this.checkoutService.getCartItems().subscribe({
      next: (items) => {
        const updatedCartItems = items
          ? items
              .map((item) => new CheckoutItem(item))
              .filter((item) => {
                return this.isBookable(
                  item.activity?.bookableUntilTheEnd,
                  item.timeslot,
                  item.numberOfAttendees
                );
              })
          : [];

        this.cartItems = updatedCartItems;
        this.isCartEmpty = this.cartItems.length === 0;

        if (!this.isCartEmpty && items[0]?.activity?.isBms) {
          this.bmsTerms = [].concat(
            ...this.cartItems.map((objet) => objet.bmsTerms).flat()
          );
          this.bmsTerms = this.bmsTerms?.filter(
            (item) => item?.requiresExplicitConsent === true
          );
        }
      },
      error: (error) => {},
    });

    this.subscriptions.add(
      this.userService.isAuthenticated().subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
        if (
          this.isBrowser &&
          this.isLoggedIn &&
          localStorage.getItem('isOpen') === 'isOpen'
        ) {
          this.isRedirect = true;
          localStorage.setItem('isOpen', 'isClose');
          this.submitCart();
        }
      })
    );

    this.subscriptions.add(
      this.cartService.isCartOpen.subscribe((isOpen) => {
        this.isCartOpen = isOpen;
      })
    );

    if (!this.showExternalPayment && this.cartItems?.length > 0) {
      this.subscriptions.add(
        this.taxEstimateService.getTaxes(this.cartItems).subscribe((taxes) => {
          this.taxes = taxes;
        })
      );
    }
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private get isLegalTermsUsageLinkDisplayEnabled(): boolean {
    return this.toggleService.isEnabled(
      this.toggleNameEnum.LegalTermsUsageLinkDisplay
    );
  }

  // Taxes always returned, check if they are the same amount, in this case we have no taxes
  public get isTaxesDisplayed(): boolean {
    return (
      this.isVatDisplayed &&
      this.taxes.totalWithTax[0]?.amount !==
        this.taxes.totalWithoutTax[0]?.amount
    );
  }

  public get isVatDisplayed(): boolean {
    return this.toggleService.isEnabled(this.toggleNameEnum.IsVatDisplayed);
  }

  public get cartIcon(): VtmnPlayIconTypeEnum {
    return VtmnPlayIconTypeEnum.ShoppingBag;
  }

  public get totalTimeslots(): number {
    return this.cartItems?.length;
  }

  public get cartSubtotal(): string {
    if (this.totalTimeslots === 1) {
      return this.translateService.instant('checkout.cart.subtotal_single');
    }
    return this.translateService.instant('checkout.cart.subtotal', {
      totalTimeslots: this.totalTimeslots?.toString(),
    });
  }

  public get cartTitle(): string {
    return this.translateService.instant('checkout.cart.label');
  }

  public get isBms(): boolean {
    return this.bmsTerms?.length > 0;
  }

  public get legalTermsTranslation(): string {
    if (this.bmsTerms && this.bmsTerms.length > 0) {
      return this.checkoutService.getBmsTermsTrans(this.bmsTerms);
    }

    if (this.isLegalTermsUsageLinkDisplayEnabled) {
      return this.translateService.instant('checkout.summary.conditions.free', {
        termsOfUse: this.legalTermsUsageServiceUrl,
      });
    }
    return this.translateService.instant(
      'checkout.summary.conditions_with_links',
      {
        legalTermsUrl: this.legalTermsUsageServiceUrl || '',
        privacyPolicyUrl: this.privacyPolicyUrl || '',
      }
    );
  }

  public get legalTermsUsageServiceUrl(): object | string {
    return this.toggleService.getVariant(
      this.toggleNameEnum.LegalTermsUsageParticipantUrl,
      legalTermsUsageServiceUrlToggle
    );
  }

  private get limitedBms(): string {
    const toggleVariant = ToggleNameEnum?.BmsCartLimit;

    return this.toggleService.getVariant(
      toggleVariant,
      '6ac850dd-248a-41de-adde-fce98954fb09'
    ) as string;
  }

  public get linkIcon(): VtmnPlayIconTypeEnum {
    return VtmnPlayIconTypeEnum.Link;
  }

  public get privacyPolicyUrl(): object | string {
    return this.toggleService.getVariantString(
      this.toggleNameEnum.PrivacyPolicyUrl,
      ''
    );
  }

  public get totalItemsInCart(): string {
    if (
      this.toggleService.isEnabled(this.toggleNameEnum.MultibookingCartLimit)
    ) {
      const organizationId =
        this.cartItems === undefined
          ? ''
          : this.cartItems[0]?.activity?.organizationIdentifier;
      if (this.limitedBms === organizationId) {
        return this.translateService.instant('checkout.cart.total.items', {
          cartTotalItems: '1',
          cartLimit: '1',
        });
      }
      return this.translateService.instant('checkout.cart.total.items', {
        cartTotalItems: this.getCartItemsNumber(),
        cartLimit: this.toggleService.getVariantString(
          this.toggleNameEnum.MultibookingCartLimit,
          ''
        ),
      });
    }
    return '';
  }

  public get showExternalPayment(): boolean {
    return this.toggleService.isEnabled(
      this.toggleNameEnum.AllowExternalPayment
    );
  }

  public get totalPrice(): string {
    if (!this.cartItems && !this.cartItems?.length) {
      return '';
    }

    const displayPricePipe = new DisplayPricePipe(
      this.currencyPipe,
      this.translateService
    );

    const currency = this.cartItems[0]?.currency;
    let totalPrice = 0;

    this.cartItems?.forEach((checkoutItem: CheckoutItem) => {
      totalPrice += toIntegerPrice(checkoutItem.totalPrice);
    });

    return displayPricePipe.transform(toDecimalPrice(totalPrice), currency);
  }

  public get cartIsFull(): boolean {
    const organizationId =
      this.cartItems === undefined
        ? ''
        : this.cartItems[0]?.activity?.organizationIdentifier;
    return (
      this.limitedBms === organizationId ||
      (this.toggleService.isEnabled(
        this.toggleNameEnum.MultibookingCartLimit
      ) &&
        Number(this.getCartItemsNumber()) >=
          Number(
            this.toggleService.getVariantString(
              this.toggleNameEnum.MultibookingCartLimit,
              ''
            )
          ))
    );
  }

  public get noCartSubmission(): boolean {
    return this.cartSubmissionDisabled === true;
  }

  public get cartButtonTranslation(): string {
    return this.checkoutService.isFreeCheckout()
      ? this.translateService.instant('checkout.cart.free.continue')
      : this.translateService.instant('checkout.cart.continue');
  }

  public get isFreeCheckout(): boolean {
    return this.checkoutService.isFreeCheckout();
  }

  public disableCartSubmission(value): void {
    this.cartSubmissionDisabled = value;
  }

  public isBookable(bookableUntilTheEnd, timeslot, numberOfAttendees): boolean {
    if (bookableUntilTheEnd) {
      return (
        timeslot.status === ('PUBLISHED' as TimeslotStatusEnum) &&
        numberOfAttendees <= timeslot.remainingAttendeeCapacity
      );
    }

    const bookingDelay =
      +this.toggleService.getVariant(this.toggleNameEnum.BookingDelay, '15') ||
      15;

    return (
      timeslot.status === ('PUBLISHED' as TimeslotStatusEnum) &&
      numberOfAttendees <= timeslot.remainingAttendeeCapacity &&
      addMinutes(new Date(timeslot.startDate), bookingDelay) >= new Date()
    );
  }

  public checkUserConnexion(): void {
    this.hasClicked = true;

    if (!this.isLoggedIn) {
      localStorage.setItem('isOpen', 'isOpen');
      this.login.emit();
      return;
    }

    this.submitCart();
  }

  public getCartItemsNumber(): string {
    this.lowerSize = this.cartItems?.length > 9;
    return this.cartItems?.length.toString();
  }

  public openCartContent(): void {
    this.cartService.openCart();
  }

  public closeCartContent(): void {
    this.cartService.closeCart();
  }

  public goToActivity(): void {
    this.closeCartContent();
    this.redirectionService.redirect(
      `/attendee.show-activity/${this.cartItems[0]?.activity?.sku}`
    );
  }

  public isActivityEvent(): boolean {
    return this.cartItems[0]?.activity.type === ActivityTypeEnum.EVENT;
  }

  private handlePostConfirmation(bookingId?: string): Promise<boolean> {
    this.deleteAllCartContent();
    this.cartService.closeCart();
    return bookingId
      ? this.checkoutService.redirectToConfirmation(bookingId)
      : this.checkoutService.redirectToConfirmation();
  }

  private submitCart(): void {
    // Is paying booking
    if (!this.showExternalPayment && !this.checkoutService.isFreeCheckout()) {
      this.cartService.closeCart();
      this.redirectionService.redirect('/checkout.billing-address');
      return;
    }
    if (this.isBms) {
      const bookingId = localStorage.getItem('bookingId');
      this.subscriptions.add(
        this.bookingService.confirm(bookingId).subscribe(() => {
          localStorage.removeItem('bookingId');
          this.handlePostConfirmation();
        })
      );
      return;
    }

    this.subscriptions.add(
      this.bookingService
        .start(this.cartItems)
        .pipe(
          mergeMap((bookingResponse) => {
            const bookingId = bookingResponse.bookingId;

            if (!this.isActivityEvent) {
              return this.bookingService
                .confirm(bookingId)
                .pipe(map(() => null));
            }

            return this.bookingService.getBookingItemId(bookingId).pipe(
              mergeMap((data) => {
                const identifier = data.items?.[0]?.identifier;
                this.bookingService.setBookingId(identifier);
                return this.bookingService
                  .confirm(bookingId)
                  .pipe(map(() => identifier));
              })
            );
          })
        )
        .subscribe((activityIdentifier?: string | null) => {
          this.handlePostConfirmation(activityIdentifier ?? undefined);
        })
    );
  }

  public deleteAllCartContent(): void {
    this.checkoutService.emptyCart();
  }
}
